import { QueryClient } from "@tanstack/react-query";

import { Cart, CartProduct, EditCartProduct } from "../types/cart";
import { Product } from "../types/product";
import posProductCore, { autoCompleteProduct, getPosProductByCode } from "./product.core";

export function normalizeScanCode(code: string): string {
  if (/\\\d{6}/.test(code)) {
    return code.substring(7);
  }
  return code;
}

export function getProductIdFromJson(code: string): string | undefined {
  try {
    const obj = JSON.parse(code);
    if (obj.id === undefined) return undefined;

    return obj.id;
  } catch (e) {
    return undefined;
  }
}

export async function resolveProductIdFromScan(code: string): Promise<string | undefined> {
  const normalized = normalizeScanCode(code.trim());
  if (!normalized) return undefined;

  const jsonId = getProductIdFromJson(normalized);
  if (jsonId) return jsonId;

  if (normalized.includes(" ")) return undefined;

  try {
    const product = await getPosProductByCode(normalized);
    if (!product) return undefined;

    return product.id;
  } catch (e) {
    return undefined;
  }
}

export async function resolveSingleProductFromScan(code: string): Promise<Product | undefined> {
  const normalized = normalizeScanCode(code.trim());
  if (!normalized) return undefined;

  const productId = await resolveProductIdFromScan(normalized);
  if (productId) {
    try {
      return await posProductCore.getPosProduct(productId);
    } catch (e) {
      return undefined;
    }
  }

  try {
    const res = await autoCompleteProduct(normalized);
    if (res.list.length === 1) {
      return res.list[0];
    }
  } catch (e) {
    return undefined;
  }

  return undefined;
}

export interface AddOrIncrementCartItemParams {
  product: Product;
  cart: Cart | undefined;
  addItem: (item: CartProduct) => void;
  updateItem: (item: EditCartProduct) => void;
  getProductWarehouseId: (product?: Product) => string | undefined;
  queryClient: QueryClient;
}

export function addOrIncrementCartItem({
  product,
  cart,
  addItem,
  updateItem,
  getProductWarehouseId,
  queryClient,
}: AddOrIncrementCartItemParams) {
  const inCart = cart?.items?.find((item) => item.product.id === product.id);

  if (!inCart) {
    const warehouseId = getProductWarehouseId(product);
    //  @ts-ignore
    addItem({ product, quantity: 1, warehouseId });
    queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
  } else {
    const updatedQuantity = inCart.quantity + 1;
    updateItem({ cartItemId: inCart.id, product, quantity: updatedQuantity, warehouseId: inCart.warehouseId });
  }
}
